<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">
    <HrSidebar :isOpen="isSidebarOpen" />

    <main class="flex-1 transition-all duration-300" :class="{ 'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen }">
      <header class="fixed top-0 w-full z-50 bg-white dark:bg-gray-800 shadow">
        <HrTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <div class="p-6 mt-16">
        <div class="flex justify-between mb-8 p-4">
          <input v-model="search" type="text" placeholder="Search users..." 
                 class="border rounded-lg px-4 py-2 w-72 focus:outline-none focus:ring-2 focus:ring-[#74bd54] dark:bg-gray-700 dark:text-white" />
          <button @click="openModal()" class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f]">
            + Add User
          </button>
        </div>

        <!-- Users Grid -->
        <div v-if="filteredUsers.length" class="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
          <div v-for="user in filteredUsers" :key="user.id" class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-lg">
            <div class="flex items-center space-x-3 mb-4">
              <div class="w-12 h-12 bg-secondary text-white flex items-center justify-center rounded-full font-bold">
                {{ user.initials }}
              </div>
              <div>
                <h3 class="font-semibold text-gray-800 dark:text-white">{{ user.name }}</h3>
                <span class="text-xs text-gray-500">{{ user.role }}</span>
              </div>
            </div>
            <p class="text-sm text-gray-500 mb-6">{{ user.email }}</p>

            <div class="flex justify-between text-sm">
              <button @click="editUser(user)" class="text-blue-600 hover:underline">Edit</button>
              <button @click="confirmDelete(user.id)" class="text-red-500 hover:underline">Delete</button>
            </div>
          </div>
        </div>

        <div v-else class="text-center text-gray-400 mt-20">No users found</div>

        <!-- Modal -->
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px]">
              <h3 class="text-lg font-semibold text-[#035a76] mb-6">
                {{ editing ? "Edit User" : "Add User" }}
              </h3>

              <form @submit.prevent="saveUser" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Select Staff Member
                  </label>
                  <Multiselect
                    v-model="selectedStaff"
                    :options="staffMembers"
                    label="applicant_name"
                    track-by="id"
                    placeholder="Search and select staff..."
                    :searchable="true"
                    :close-on-select="true"
                    class="w-full"
                  />
                </div>

                <!-- Full Name (Auto-filled) -->
                <input
                  v-model="form.name"
                  type="text"
                  placeholder="Full Name"
                  readonly
                  class="w-full border rounded p-2 bg-gray-100 dark:bg-gray-700 dark:text-white cursor-not-allowed"
                />

                <!-- New Email -->
                <input
                  v-model="form.email"
                  type="email"
                  placeholder="New Email Address"
                  required
                  class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
                />

                <!-- Password -->
                <input
                  v-model="form.password"
                  type="password"
                  placeholder="Password"
                  :required="!editing"
                  class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
                />

                <!-- Role (Department Name) -->
                <div>
                  <label class="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Role (Department)
                  </label>
                  <input
                  readonly
                    v-model="form.role"
                    type="text"
                    class="w-full border rounded p-2 bg-gray-100 dark:bg-gray-700 dark:text-white cursor-not-allowed"
                  />
                </div>

                <div class="flex justify-end space-x-3 pt-4">
                  <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-400">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    :disabled="isLoading"
                    class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f] disabled:opacity-60 flex items-center gap-2 min-w-[100px] justify-center"
                  >
                    <svg v-if="isLoading" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{{ isLoading ? 'Saving...' : 'Save' }}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Transition>
      </div>
    </main>
  </div>

  <ConfirmModal
    :show="showDeleteModal"
    title="Delete User"
    message="Are you sure you want to delete this user? This cannot be undone."
    confirmText="Delete"
    cancelText="Cancel"
    @confirm="handleDelete"
    @cancel="showDeleteModal = false"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Multiselect from 'vue-multiselect';
import api from "@/services/api";
import HrSidebar from "@/components/hr/HrSidebar.vue";
import HrTopbar from "@/components/hr/HrTopbar.vue";
import ConfirmModal from "@/components/hr/ConfirmModal.vue";
import { useToast } from 'vue-toastification';

const toast = useToast();
const isLoading = ref(false);

const currentPageTitle = ref("Users");
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);

const showModal = ref(false);
const editing = ref(false);
const search = ref("");
const users = ref([]);
const staffMembers = ref([]);
const selectedStaff = ref(null);

const showDeleteModal = ref(false);
const itemToDelete = ref(null);

const form = ref({
  id: null,
  applicant_id: null,
  name: "",
  email: "",
  password: "",
  role: ""
});

// Fetch Staff
// const fetchStaffMembers = async () => {
//   try {
//     const response = await api.get('get_hired_applicants.php');
//     staffMembers.value = response.data.data.map(item => ({
//       id: item.applicant_id,
//       applicant_name: item.applicant_name,
//       department_name: item.department?.department_name || 'Staff'
//     }));
//   } catch (error) {
//     console.error('Error fetching staff:', error);
//     toast.error("Failed to load staff list");
//   }
// };

const fetchStaffMembers = async () => {
  try {
    const response = await api.get('get_hired_applicants.php');
    
    staffMembers.value = response.data.data
      .filter(item => {
        const userType = 
          (item.user_type || 
           item.role || 
           item.department?.department_name || 
           item.userType || 
           '').toString().trim().toUpperCase();

        return userType === 'HR' || 
               userType === 'HUMAN RESOURCES' ||
               userType.includes('HR');
      })
      .map(item => ({
        id: item.applicant_id,
        applicant_name: item.applicant_name,
        department_name: item.department?.department_name || 'Staff'
      }));

  } catch (error) {
    console.error('Error fetching staff:', error);
    toast.error("Failed to load staff list");
  }
};

// Watch for selection change (More reliable than @select)
watch(selectedStaff, (newVal) => {
  if (newVal) {
    form.value.applicant_id = newVal.id;
    form.value.name = newVal.applicant_name || '';        // ← This should now work
    form.value.role = newVal.department_name || 'Staff';
  }
});

const fetchUsers = async () => {
  try {
    const response = await api.get("Users.php");
    
    users.value = response.data
      .filter(user => {
        const type = (user.user_type || user.role || "").trim();
        return type === "HR" || type.toLowerCase() === "human resources";
      })
      .map(user => ({
        id: user.id,
        name: user.full_name,
        email: user.email,
        role: user.user_type || user.role,
        initials: getInitials(user.full_name)
      }));
  } catch (error) {
    console.error("Error fetching users:", error);
    toast.error("Failed to load users");
  }
};

const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || '';


const filteredUsers = computed(() => users.value.filter(u => 
  (u.name || '').toLowerCase().includes(search.value.toLowerCase()) ||
  (u.email || '').toLowerCase().includes(search.value.toLowerCase()) ||
  (u.role || '').toLowerCase().includes(search.value.toLowerCase())
));

const openModal = () => {
  editing.value = false;
  selectedStaff.value = null;
  form.value = { id: null, applicant_id: null, name: "", email: "", password: "", role: "" };
  showModal.value = true;
};

const editUser = (user) => {
  editing.value = true;
  form.value = { ...user };
  showModal.value = true;
};

const saveUser = async () => {
  if (isLoading.value) return;
  isLoading.value = true;

  try {
    const payload = { ...form.value };
    let response;

    if (editing.value) {
      response = await api.put("Users.php", payload);
    } else {
      response = await api.post("Users.php", payload);
    }

    toast.success(editing.value ? "User updated successfully!" : "User created successfully!");
    closeModal();
    fetchUsers();
  } catch (error) {
    toast.error(error.response?.data?.message || error.message || "Failed to save");
  } finally {
    isLoading.value = false;
  }
};
const confirmDelete = (id) => { itemToDelete.value = id; showDeleteModal.value = true; };
const handleDelete = async () => {
  try {
    await api.delete("Users.php", { data: { id: itemToDelete.value } });
    users.value = users.value.filter(u => u.id !== itemToDelete.value);
    toast.success("User deleted successfully!");
  } catch (err) {
    toast.error("Failed to delete user.");
  } finally {
    showDeleteModal.value = false;
  }
};
const closeModal = () => {
  showModal.value = false;
  selectedStaff.value = null;
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  document.documentElement.classList.toggle("dark", isDarkMode.value);
};

onMounted(() => {
  fetchUsers();
  fetchStaffMembers();
});
</script>

<style>
.modal-enter-active, .modal-leave-active { transition: all .25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; transform: scale(0.9); }
</style>